/**
 * Geography helpers for the planner. Deliberately free of any mapping library —
 * these are straight arithmetic over the coordinates Salesforce already holds.
 */

/** Somewhere with coordinates — a booked visit, or a candidate account. */
export interface Located {
    lat: number;
    lng: number;
}

/** Straight-line miles. Not drive time; label it as such wherever it is shown. */
export const distanceMiles = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const R = 3958.8;
    const rad = (d: number) => (d * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
};

/**
 * Salesforce's BillingGeocodeAccuracy bands that are too coarse to pin as a point.
 *
 * Measured Sep 2026 against Address-accuracy rows sharing a postcode: Block, ExtendedZip and
 * Street all land within ~150 m of the true point, while City is 1.8 miles out at the median
 * and 6.7 at the 90th percentile — so the cliff is where this line puts it. City-level
 * accounts still get a distance, marked approximate with a "~", rather than being dropped:
 * 14% of owed work is City-level, and a rough figure the planner can see and judge beats
 * none at all. Tolerable while distances are straight-line regardless.
 */
export const isApproxAccuracy = (acc?: string | null) => !acc || /^(city|state|country|unknown)$/i.test(acc);

/**
 * A box around the UK and Ireland — Shetland to the Scillies, west Kerry to Kent.
 * Anything outside it is an overseas site or a bad geocode; either way it is not
 * pairable work and it would wreck the map bounds if it were pinned.
 */
export const isInUkOrIreland = (lat: number, lng: number) =>
    lat >= 49.8 && lat <= 61.1 && lng >= -11.0 && lng <= 2.2;
